import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import { apiRequest } from "../../services/api";
import { encryptData } from "../Common/encryptionUtils";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

import showPasswordIcon from "../../assets/ph_eye.png";
import hidePasswordIcon from "../../assets/eye.png";
import { AccessLog } from "../../Utils/AccessLog";

const validationSchema = Yup.object().shape({
  currentPassword: Yup.string().required("Current Password is required"),
  newPassword: Yup.string()
    .min(8, "Password must be at least 8 characters long")
    .matches(/[A-Za-z]/, "Password must contain at least one letter")
    .matches(/[0-9]/, "Password must contain at least one number")
    .required("New Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
    .required("Confirm Password is required"),
});

const AdminChangePassword = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    setLoading(true);

    try {
      const authToken = localStorage.getItem("authToken");
      const headers = { Authorization: `Bearer ${authToken}` };

      const customerDetails = JSON.parse(
        localStorage.getItem("customerDetails")
      );

      // 🔍 Step 1: Validate Current Password from Backend
      const validateResponse = await apiRequest(
        "web_api/admin/verifyPassword",
        "POST",
        {
          email: encryptData(customerDetails?.email),
          password: encryptData(values.currentPassword),
        },
        headers
      );
      console.log("validateResponse-->", validateResponse?.data);
      if (validateResponse?.data?.status_code !== 200) {
        setFieldError("currentPassword", "Incorrect current password");
        // toast.error("Incorrect current password");
        setLoading(false);
        setSubmitting(false);
        return;
      }

      // ✅ Step 2: Proceed with Password Change
      const payload = {
        email: encryptData(customerDetails?.email),
        new_password: encryptData(values.newPassword),
      };

      const response = await apiRequest(
        "web_api/admin/customerAccountChangePassword",
        "POST",
        payload,
        headers
      );

      if (response?.data?.status_code === 200) {
        onClose();
        toast.success(response?.data?.message);
      } else {
        toast.error(response?.data?.message || "Failed to change password.");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "An error occurred.");
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleTogglePassword = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Change Password</DialogTitle>
      <DialogContent>
        <Formik
          initialValues={{
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form>
              <Field
                as={TextField}
                fullWidth
                label="Current Password"
                type={showPassword.current ? "text" : "password"}
                name="currentPassword"
                margin="dense"
                error={Boolean(
                  errors.currentPassword && touched.currentPassword
                )}
                helperText={touched.currentPassword && errors.currentPassword}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: "0.9rem",
                    fontWeight: "600",
                    color: "#444444",
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => handleTogglePassword("current")}
                      >
                        <img
                          src={
                            showPassword.current
                              ? showPasswordIcon
                              : hidePasswordIcon
                          }
                          alt="Toggle Password"
                          style={{ width: 18, height: 18, marginRight: "-8px" }}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* New Password */}
              <Field
                as={TextField}
                fullWidth
                label="New Password"
                type={showPassword.new ? "text" : "password"}
                name="newPassword"
                margin="dense"
                error={Boolean(errors.newPassword && touched.newPassword)}
                helperText={touched.newPassword && errors.newPassword}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: "0.9rem",
                    fontWeight: "600",
                    color: "#444444",
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => handleTogglePassword("new")}>
                        <img
                          src={
                            showPassword.new
                              ? showPasswordIcon
                              : hidePasswordIcon
                          }
                          alt="Toggle Password"
                          style={{ width: 18, height: 18, marginRight: "-8px" }}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Confirm Password */}
              <Field
                as={TextField}
                fullWidth
                label="Confirm Password"
                type={showPassword.confirm ? "text" : "password"}
                name="confirmPassword"
                margin="dense"
                error={Boolean(
                  errors.confirmPassword && touched.confirmPassword
                )}
                helperText={touched.confirmPassword && errors.confirmPassword}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: "0.9rem",
                    fontWeight: "600",
                    color: "#444444",
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => handleTogglePassword("confirm")}
                      >
                        <img
                          src={
                            showPassword.confirm
                              ? showPasswordIcon
                              : hidePasswordIcon
                          }
                          alt="Toggle Password"
                          style={{ width: 18, height: 18, marginRight: "-8px" }}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <DialogActions>
                <Button onClick={onClose} color="secondary">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting || loading}
                >
                  {loading ? <CircularProgress size={24} /> : "Change Password"}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default AdminChangePassword;
